/**
 * Step state from facts: probe results for device steps, account facts for
 * user steps. Used by the app (what to show, what to report) and the admin
 * view (a device's reported permissions as a grid).
 */

import type { DevicePermissionReport, PermState } from './report'
import type { SetupLevel, SetupRequirement, SetupStepId } from './requirements'

/**
 * - `done`    — granted / answered / read.
 * - `todo`    — can be asked or done from the app.
 * - `blocked` — turned off in phone or browser settings; only Settings can fix it.
 * - `unknown` — not checked yet, or the check failed.
 */
export type SetupStepState = 'done' | 'todo' | 'blocked' | 'unknown'

export type SetupFacts = {
  permissions: Partial<Omit<DevicePermissionReport, 'v'>>
  /** The call-recording step is answered (see `isRecordingConsentStepDone`). Null = not known yet. */
  recordingConsentDone: boolean | null
  /** The session-recording explainer this person needs has been read. */
  sessionRecordingExplainerSeen: boolean
  /** Biometric app lock is on for this install. */
  appLockOn: boolean
}

function fromPerm(state: PermState | undefined): SetupStepState {
  switch (state) {
    case 'granted':
      return 'done'
    case 'denied':
      return 'blocked'
    case 'prompt':
      return 'todo'
    default:
      return 'unknown'
  }
}

export function setupStepState(id: SetupStepId, facts: SetupFacts): SetupStepState {
  const p = facts.permissions
  switch (id) {
    case 'notifications':
      return fromPerm(p.notifications)
    case 'calls-channel':
      // A muted channel is fixed in channel settings, so "off" reads as blocked.
      return fromPerm(p.callsChannel)
    case 'lock-screen-calls':
      return fromPerm(p.fullScreenIntent)
    case 'battery':
      // The battery dialog can always be shown again: never "blocked".
      return p.batteryExempt === 'denied' ? 'todo' : fromPerm(p.batteryExempt)
    case 'microphone':
      return fromPerm(p.microphone)
    case 'camera':
      return fromPerm(p.camera)
    case 'location':
      return fromPerm(p.location)
    case 'location-precise':
      // Location itself comes first; the precise switch means nothing before that.
      if (p.location !== 'granted') return 'todo'
      if (p.locationPrecise === 'precise') return 'done'
      if (p.locationPrecise === 'approximate') return 'blocked'
      return 'unknown'
    case 'recording-consent':
      return facts.recordingConsentDone == null ? 'unknown' : facts.recordingConsentDone ? 'done' : 'todo'
    case 'session-recording-info':
      return facts.sessionRecordingExplainerSeen ? 'done' : 'todo'
    case 'app-lock':
      return facts.appLockOn ? 'done' : 'todo'
  }
}

export type SetupRow = { id: SetupStepId; level: SetupLevel; state: SetupStepState }

export type SetupSummary = {
  rows: SetupRow[]
  /** Required and not done (`todo` / `blocked`). A failed check is not counted. */
  outstanding: SetupStepId[]
  /** Optional, asked before, not done. */
  skipped: SetupStepId[]
  /** Optional or info, never asked, not done: part of the next first-run walk. */
  notYetAsked: SetupStepId[]
}

export function summarizeSetup(
  requirements: readonly SetupRequirement[],
  facts: SetupFacts,
  optionalAsked: ReadonlySet<SetupStepId> | readonly SetupStepId[] = [],
): SetupSummary {
  const asked = optionalAsked instanceof Set ? optionalAsked : new Set(optionalAsked)
  const rows = requirements.map((r) => ({ ...r, state: setupStepState(r.id, facts) }))
  const open = (row: SetupRow) => row.state === 'todo' || row.state === 'blocked'
  return {
    rows,
    outstanding: rows.filter((r) => r.level === 'required' && open(r)).map((r) => r.id),
    skipped: rows
      .filter((r) => r.level === 'optional' && r.state !== 'done' && asked.has(r.id))
      .map((r) => r.id),
    notYetAsked: rows
      .filter((r) => r.level !== 'required' && r.state !== 'done' && !asked.has(r.id))
      .map((r) => r.id),
  }
}
